// components/products/ProductsTable.jsx
import React from 'react'
import { fmtMoney, cls } from '../../utils/helpers.js'
import { VariantsPanel } from './VariantsPanel.jsx'

export function ProductsTable({
  products,
  onRowClick,
  displayStock,
  unitPrice,
  stockMin,
  adjustStock,
  saving,
  openId,
  editing,
  setEditing,
  saveEdit,
  applyStockDelta,
  deleteProduct,
  editError,
  editErrors,
  stockErrors,
  editValidation,
  stockValidation
}) {
  const inputBase =
    'w-full border border-slate-200 bg-white rounded-xl px-3 py-2 text-sm outline-none transition focus:ring-2 focus:ring-slate-200 focus:border-slate-300'
  const inputError =
    'w-full border border-rose-300 bg-rose-50 rounded-xl px-3 py-2 text-sm outline-none transition focus:ring-2 focus:ring-rose-200 focus:border-rose-400'
  if (!products.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
        <div className="text-4xl mb-4">📦</div>
        <div className="mt-2 font-extrabold text-slate-900">Aucun produit trouvé</div>
        <div className="mt-1 text-sm text-slate-600">Essaie une autre recherche ou enlève les filtres.</div>
      </div>
    )
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto shadow-sm">
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
              Produit
            </th>
            <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
              SKU
            </th>
            <th className="px-6 py-3 text-right text-xs font-bold text-slate-600 uppercase tracking-wider">
              Prix
            </th>
            <th className="px-6 py-3 text-right text-xs font-bold text-slate-600 uppercase tracking-wider">
              Stock
            </th>
            <th className="px-6 py-3 text-right text-xs font-bold text-slate-600 uppercase tracking-wider">
              Valeur
            </th>
            <th className="px-6 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
              État
            </th>
            <th className="px-6 py-3 text-right text-xs font-bold text-slate-600 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {products.map((p) => {
            const st = displayStock(p)
            const min = stockMin(p)
            const low = st <= min && (min > 0 || st > 0)
            const zero = st === 0
            const price = unitPrice(p)
            const value = st * price

            const isOpen = openId === p.id

            return (
              <React.Fragment key={p.id}>
                <tr
                  className={cls(
                    "transition-colors",
                    isOpen ? "bg-slate-50" : "hover:bg-slate-50"
                  )}
                >
                  <td className="px-6 py-4">
                    <div className="font-bold text-slate-900">{p.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-600">{p.sku || '—'}</div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="font-semibold text-slate-900">{fmtMoney(price)}</div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="font-bold text-slate-900 tabular-nums">{st}</div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="font-semibold text-slate-900">{fmtMoney(value)}</div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {zero ? (
                      <span className="inline-flex px-2 py-1 text-xs font-bold rounded-full bg-rose-50 text-rose-700 border border-rose-100">
                        Rupture
                      </span>
                    ) : low ? (
                      <span className="inline-flex px-2 py-1 text-xs font-bold rounded-full bg-amber-50 text-amber-700 border border-amber-100">
                        Stock faible
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-1 text-xs font-bold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                        OK
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => adjustStock(p.id, +1)}
                        disabled={saving}
                        className={cls(
                          'px-2 py-1 rounded-lg text-xs font-bold border transition',
                          saving
                            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                            : 'bg-white border-slate-200 hover:bg-slate-50 text-slate-900'
                        )}
                        title="Ajouter 1"
                      >
                        +1
                      </button>
                      <button
                        onClick={() => adjustStock(p.id, -1)}
                        disabled={saving || st <= 0}
                        className={cls(
                          'px-2 py-1 rounded-lg text-xs font-bold border transition',
                          saving || st <= 0
                            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                            : 'bg-white border-slate-200 hover:bg-slate-50 text-slate-900'
                        )}
                        title="Retirer 1"
                      >
                        -1
                      </button>
                      <button
                        onClick={() => onRowClick(p.id)}
                        disabled={saving}
                        className={cls(
                          'px-3 py-1 rounded-lg text-xs font-bold border transition',
                          saving
                            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                            : isOpen
                            ? 'bg-slate-900 border-slate-900 text-white hover:bg-slate-950'
                            : 'bg-white border-slate-200 hover:bg-slate-50 text-slate-900'
                        )}
                      >
                        {isOpen ? 'Fermer' : 'Gérer'}
                      </button>
                    </div>
                  </td>
                </tr>

                {/* Panneau d'édition expandable */}
                {isOpen && (
                  <tr className="bg-slate-50">
                    <td colSpan="7" className="px-6 py-6">
                      <div className="space-y-4">
                        {editError && (
                          <div className="p-3 border border-rose-200 rounded-2xl bg-rose-50 text-rose-700 text-sm font-semibold">
                            {editError}
                          </div>
                        )}

                        {/* Prix & seuil */}
                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="text-sm font-extrabold text-slate-900 mb-3">Prix & seuil</div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <div className="text-xs font-semibold text-slate-500">Prix</div>
                              <input
                                type="number"
                                min="0"
                                className={editErrors?.unit_price ? inputError : inputBase}
                                value={editing.unit_price}
                                onChange={(e) => {
                                  setEditing((ed) => ({ ...ed, unit_price: Number(e.target.value) }))
                                  editValidation.clearFieldError('unit_price')
                                }}
                              />
                              {editErrors?.unit_price && <div className="text-xs text-rose-600 mt-1">{editErrors.unit_price}</div>}
                            </div>

                            <div>
                              <div className="text-xs font-semibold text-slate-500">Stock min</div>
                              <input
                                type="number"
                                min="0"
                                className={editErrors?.stock_min ? inputError : inputBase}
                                value={editing.stock_min}
                                onChange={(e) => {
                                  setEditing((ed) => ({ ...ed, stock_min: Number(e.target.value) }))
                                  editValidation.clearFieldError('stock_min')
                                }}
                              />
                              {editErrors?.stock_min && <div className="text-xs text-rose-600 mt-1">{editErrors.stock_min}</div>}
                            </div>

                            <div className="flex items-end">
                              <button
                                onClick={() => saveEdit(p)}
                                disabled={saving}
                                className={cls(
                                  'w-full px-4 py-2 rounded-xl text-sm font-black text-white transition',
                                  saving ? 'bg-slate-300 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700 active:scale-[0.99]'
                                )}
                              >
                                Sauver
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Ajuster stock (manuel) */}
                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="text-sm font-extrabold text-slate-900 mb-3">Ajuster stock</div>
                          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                            <div className="md:col-span-3">
                              <div className="text-xs font-semibold text-slate-500">Delta</div>
                              <input
                                type="number"
                                step="1"
                                className={stockErrors?.delta ? inputError : inputBase}
                                value={editing.stock_delta}
                                onChange={(e) => {
                                  setEditing((ed) => ({ ...ed, stock_delta: Number(e.target.value) }))
                                  stockValidation.clearFieldError('delta')
                                }}
                                placeholder="+10 / -3"
                              />
                              {stockErrors?.delta && <div className="text-xs text-rose-600 mt-1">{stockErrors.delta}</div>}
                            </div>

                            <div className="md:col-span-6">
                              <div className="text-xs font-semibold text-slate-500">Motif</div>
                              <input
                                className={inputBase}
                                value={editing.stock_reason}
                                onChange={(e) => setEditing((ed) => ({ ...ed, stock_reason: e.target.value }))}
                                placeholder="inventaire, casse, correction…"
                              />
                            </div>

                            <div className="md:col-span-3">
                              <button
                                onClick={() => applyStockDelta(p)}
                                disabled={saving}
                                className={cls(
                                  'w-full px-4 py-2 rounded-xl text-sm font-black text-white transition',
                                  saving ? 'bg-slate-300 cursor-not-allowed' : 'bg-slate-900 hover:bg-slate-950 active:scale-[0.99]'
                                )}
                              >
                                Appliquer
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Variantes */}
                        <VariantsPanel productId={p.id} />

                        {/* Suppression */}
                        <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="text-xs text-slate-500">
                            Suppression possible seulement si <span className="font-semibold text-slate-900">stock = 0</span>.
                          </div>
                          <button
                            onClick={() => deleteProduct(p)}
                            disabled={saving || st > 0}
                            className={cls(
                              'px-4 py-2 rounded-xl text-sm font-black transition',
                              (saving || st > 0)
                                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                : 'bg-rose-600 text-white hover:bg-rose-700'
                            )}
                            title={st > 0 ? 'Mets le stock à 0 avant suppression' : 'Supprimer définitivement'}
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
